import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Image, Video, Trash2, Download, Clock, Camera } from "lucide-react";
import { toast } from "sonner";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function MonitoredFiles() {
  const [activeTab, setActiveTab] = useState("images");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMedia = async (type) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/captured-media?type=${type}`, { headers: getHeaders(), withCredentials: true });
      setItems(res.data.items || []);
    } catch { setItems([]); }
    setLoading(false);
  };

  useEffect(() => { fetchMedia(activeTab); }, [activeTab]);

  const deleteMedia = async (id) => {
    try {
      await axios.delete(`${API}/captured-media/${id}`, { headers: getHeaders(), withCredentials: true });
      toast.success("Deleted");
      setItems(prev => prev.filter(i => i.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const downloadMedia = (dataUrl, name) => {
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = name || "download";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-app flex items-center gap-2">
          <Camera className="w-5 h-5 text-cyan-400" /> Monitored Files
        </h2>
        <div className="flex gap-1 bg-surface-item rounded-lg p-0.5">
          <button onClick={() => setActiveTab("image")} className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${activeTab === "image" ? "bg-cyan-600 text-white" : "text-app-secondary hover:text-app"}`}>
            <Image className="w-3.5 h-3.5 inline mr-1" />Images
          </button>
          <button onClick={() => setActiveTab("video")} className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${activeTab === "video" ? "bg-cyan-600 text-white" : "text-app-secondary hover:text-app"}`}>
            <Video className="w-3.5 h-3.5 inline mr-1" />Videos
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-app-secondary" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-10">
          <Camera className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-app-secondary">No {activeTab === "image" ? "images" : "videos"} captured yet</p>
          <p className="text-xs text-slate-500 mt-1">Capture screenshots or record clips from Live Drone broadcasts</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((item) => (
            <Card key={item.id} className="bg-app-card border-app overflow-hidden">
              {item.type === "image" ? (
                <div className="relative aspect-video bg-black">
                  <img src={item.data_url} alt={item.stream_name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="relative aspect-video bg-black">
                  <video controls className="w-full h-full" src={item.data_url} />
                </div>
              )}
              <div className="p-2.5 space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-app truncate">{item.stream_name}</p>
                  <Badge className={`text-[9px] ${item.type === "image" ? "bg-blue-600/20 text-blue-300" : "bg-purple-600/20 text-purple-300"}`}>
                    {item.type === "image" ? "Image" : "Video"}
                  </Badge>
                </div>
                <div className="flex items-center gap-1 text-[9px] text-app-secondary">
                  <Clock className="w-2.5 h-2.5" />
                  {item.created_at ? new Date(item.created_at).toLocaleString() : ""}
                </div>
                <div className="flex gap-1.5 pt-1">
                  <button onClick={() => downloadMedia(item.data_url, `${item.stream_name}_${item.id}.${item.type === "video" ? "webm" : "png"}`)}
                    className="flex-1 text-[10px] bg-cyan-700 hover:bg-cyan-600 text-white py-1 rounded flex items-center justify-center gap-1">
                    <Download className="w-3 h-3" /> Download
                  </button>
                  <button onClick={() => deleteMedia(item.id)}
                    className="text-[10px] bg-red-700/50 hover:bg-red-600 text-white px-2 py-1 rounded flex items-center gap-1">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
